import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Alert,
  Button,
  Form,
  Input,
  Select,
  Space,
  Spin,
  TimePicker,
  Upload,
  Card,
  Typography,
  Row,
  Col,
  message,
  notification,
} from 'antd';
import {
  AimOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CameraOutlined,
  FileTextOutlined,
  SignatureOutlined,
  ToolOutlined,
  BuildOutlined,
  UploadOutlined,
  UserOutlined,
} from '@ant-design/icons';
import TextArea from 'antd/lib/input/TextArea';
import { getVouchersReasons, postAttentionVoucher } from '../../../../api/attentionVoucher';
import Signature from '../../../global/Form/Signature/Signature';
import { API_PATH } from '../../../../config';
import { captureGpsDetailed } from 'hooks/useVisita';
import { gpsReasonMessage } from './gpsReasonMessage';
import { estadosFinalesAscensor } from 'assets/data/FinishStates';
import dayjs from 'libs/dayjs';
import notificationApi from 'utils/notificationApi';
import SelectClient from 'components/global/Form/SelectClient';
import RutRecepcionistaField from 'components/specialist/Recepcionista/RutRecepcionistaField';
import { getAccessTokenApi } from '../../../../api/auth';
import AscensorInlineSelect from 'components/selects/AscensorInlineSelect';
import { resolveNamesToIds } from './resolveNamesToIds';
import { getToApi } from '../../../../api/axios';
import HallazgosVoucherSection from './HallazgosVoucherSection';
import { isEmergencyReason } from './isEmergencyReason';
import { improveTexts } from '../../../../api/aiText';
import RevisarRedaccionDrawer from '../../../mantenciones/RevisarRedaccionDrawer';
import { juntarTextosVoucher, aplicarTextosVoucher } from './voucherTexts';
import { uploadHallazgoFotos } from 'components/hallazgos/uploadHallazgoFotos';
import useVoucherSend from 'PDF/AttentionVoucher/useVoucherSend';

const format = 'HH:mm';

/**
 * Arma el body del POST de creación de comprobante mezclando los valores del
 * formulario con los campos calculados (timestamps, GPS de inicio y fin,
 * IDs de imágenes y ascensores, hallazgos).
 * Exportada para que los tests puedan ejercitar la lógica pura sin montar el componente.
 *
 * @param {object} opts
 * @param {object} opts.body           - Valores crudos del formulario (Form.getFieldsValue)
 * @param {string} opts.attentionInit  - Hora de inicio formateada 'HH:mm'
 * @param {string} opts.attentionFinish - Hora de fin formateada 'HH:mm'
 * @param {string} opts.startedAt      - Timestamp ISO-like 'YYYY-MM-DD HH:mm:ss'
 * @param {string} opts.completedAt    - Timestamp ISO-like 'YYYY-MM-DD HH:mm:ss'
 * @param {{ lat: number|null, lng: number|null }} opts.inicioGps - GPS al abrir el form
 * @param {number[]} opts.imageIds     - IDs de imágenes ya subidas
 * @param {boolean}  opts.allResolved  - Si todos los ascensores resolvieron a FK
 * @param {number[]} opts.ids          - IDs de ascensores (solo si allResolved)
 * @param {boolean}  opts.esEmergencia - Si la razón es de emergencia
 * @param {object[]} opts.hallazgosClean - Hallazgos sin _key
 * @returns {object}
 */
export function buildVoucherBody({
  body,
  attentionInit,
  attentionFinish,
  startedAt,
  completedAt,
  inicioGps,
  imageIds,
  allResolved,
  ids,
  esEmergencia,
  hallazgosClean,
}) {
  return {
    ...body,
    attention_init: attentionInit,
    attention_finish: attentionFinish,
    started_at: startedAt,
    completed_at: completedAt,
    inicio_visita_lat: inicioGps.lat,
    inicio_visita_lng: inicioGps.lng,
    images: imageIds,
    ...(allResolved && ids.length ? { id_ascensores: ids } : {}),
    ...(esEmergencia && hallazgosClean.length ? { hallazgos: hallazgosClean } : {}),
  };
}

/**
 * Auto-envío al cerrar el create: dispara `sendVoucher` en modo automático
 * (recipients null) solo si el `query` de la respuesta trae el flag del
 * cliente en ON y el id del comprobante recién creado. Best-effort: un fallo
 * en el envío no debe interrumpir el flujo — el comprobante ya quedó creado y
 * el envío se puede reintentar manual desde el listado.
 * Exportada para tests — permite verificar el gate sin montar el form completo.
 *
 * @param {{ envio_auto_comprobante?: boolean, idVoucher?: number }|undefined} query
 * @param {(voucher: {id: number}, recipients: null) => Promise<void>} sendVoucher
 */
export async function autoSendVoucherIfEnabled(query, sendVoucher) {
  if (!query?.envio_auto_comprobante || !query?.idVoucher) return;
  try {
    await sendVoucher({ id: query.idVoucher }, null);
  } catch {
    // best-effort: el comprobante ya quedó creado; el envío se puede reintentar manual
  }
}

function AddAttentionVoucherForm(props) {
  const { setReload, setModalVisible } = props;
  const { sendVoucher } = useVoucherSend();

  const [voluchersReason, setVouchersReason] = useState();
  const [voucherReasonsNew, setVoucherReasonsNew] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [clientAscensores, setClientAscensores] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [hallazgos, setHallazgos] = useState([]);
  const [form] = Form.useForm();
  // Acota la sugerencia del recepcionista al cliente de la visita: sin esto, el RUT
  // se buscaría contra toda la empresa y la app pasaría a ser un buscador de nombres.
  const clienteSeleccionado = Form.useWatch('id_client', form);

  // GPS de inicio de visita (best-effort): se captura al montar el form.
  // { status: 'capturing'|'ok'|'error', lat, lng, reason }
  // El comprobante puede guardarse igual con lat/lng null — GPS es informativo.
  const [gps, setGps] = useState({ status: 'capturing', lat: null, lng: null, reason: null });

  const registrarUbicacion = async () => {
    setGps((prev) => ({ ...prev, status: 'capturing' }));
    const r = await captureGpsDetailed();
    setGps({ status: r.ok ? 'ok' : 'error', lat: r.lat, lng: r.lng, reason: r.reason });
  };

  useEffect(() => {
    registrarUbicacion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Alias para compatibilidad con buildVoucherBody (espera { lat, lng })
  const inicioGps = { lat: gps.lat, lng: gps.lng };

  // Map keyed by hallazgo._key → ref de HallazgoFotosUpload (modo create).
  // Stable entre renders; las claves se registran/eliminan via el callback-ref
  // en HallazgosVoucherSection. Al submit se itera en el orden de `hallazgos`
  // (mismo orden enviado al backend), que garantiza alineación con hallazgos[].
  const fotosRefsMap = useRef(new Map());

  // Estado para el drawer de revisión IA
  const [revisionOpen, setRevisionOpen] = useState(false);
  const [revisionLoading, setRevisionLoading] = useState(false);
  const [revisionItems, setRevisionItems] = useState([]);
  // Guarda el body + hallazgos listos para post mientras el usuario revisa
  const pendingBodyRef = useRef(null);

  // Observar el campo reason para detectar si es emergencia
  const selectedReason = Form.useWatch('reason', form);
  // Observar los equipos seleccionados para resolverlos a {nombre, id}
  const selectedEquipment = Form.useWatch('equipment', form);

  // Determinar si la razón seleccionada es de emergencia (case-insensitive)
  const esEmergencia = useMemo(
    () => isEmergencyReason(selectedReason, voucherReasonsNew),
    [selectedReason, voucherReasonsNew]
  );

  // Equipos seleccionados resueltos a {nombre, id} (solo los que tienen id en el catálogo)
  const equiposSeleccionados = useMemo(() => {
    if (!Array.isArray(selectedEquipment) || !clientAscensores.length) return [];
    return selectedEquipment
      .map((nombre) => {
        const found = clientAscensores.find((a) => a.nombre === nombre);
        return found ? { nombre: found.nombre, id: found.id } : null;
      })
      .filter(Boolean);
  }, [selectedEquipment, clientAscensores]);

  const token = getAccessTokenApi();
  const normFile = (e) => {
    if (Array.isArray(e)) {
      return e;
    }
    return e?.fileList;
  };

  const beforeUpload = (file) => {
    const mb = 40;
    const isLt = file.size / 1024 / 1024 < mb; // Tamaño menor a 40 MB
    if (!isLt) {
      message.error(`El archivo debe ser menor que ${mb}MB`);
    }
    return isLt;
  };

  const handleUploadChange = (info) => {
    const { status, response } = info.file;

    if (status === 'done') {
      if (response && response.success) {
        message.success(`${info.file.name} subido exitosamente`);
        // El fileList ya contiene la respuesta del servidor con el ID
        // No necesitamos modificar nada, la estructura ya es correcta
      } else {
        message.error(`${info.file.name} falló al subir`);
      }
    } else if (status === 'error') {
      message.error(`${info.file.name} falló al subir`);
    }
  };
  useEffect(() => {
    getVouchersReasons().then((result) => {
      setVouchersReason(result.reasons);
    });
    // También cargar desde el endpoint nuevo que tiene es_emergencia
    getToApi('/voucher-reasons')
      .then((reasons) => {
        if (Array.isArray(reasons)) setVoucherReasonsNew(reasons);
      })
      .catch(() => {
        // silencioso: si falla, es_emergencia queda false y no se muestra la sección
      });
  }, []);

  // Función para manejar la selección del cliente.
  // AscensorInlineSelect carga su propia lista via onListLoaded cuando cambia idClient.
  const handleClientChange = (clientId) => {
    setSelectedClientId(clientId);
    // Limpiar selección de equipos al cambiar el cliente
    form.setFieldValue('equipment', []);
    if (!clientId) setClientAscensores([]);
  };

  // Ejecuta el POST final, sube fotos pendientes por hallazgo, y maneja reset.
  // hallazgosOrden: array de hallazgos en el mismo orden enviado al backend.
  // Sirve para mapear hallazgosOrden[i]._key → resp.query.hallazgos[i].id
  // y así llamar uploadPending(id) sobre el ref correcto.
  const doPost = async (body, hallazgosOrden = []) => {
    setSubmitting(true);
    try {
      const postVoucherResult = await postAttentionVoucher(body);
      // notificationApi con enabled=false: NO dispara el toast de éxito todavía
      // (sí muestra warning/error). El "listo" se confirma recién cuando TODO
      // el trabajo terminó —incluida la subida de fotos— para no avisar éxito
      // mientras siguen corriendo requests en segundo plano.
      if (notificationApi(postVoucherResult, false)) {
        // Auto-envío al cierre: solo si el cliente tiene el flag ON. El backend
        // re-verifica el flag y deriva el destinatario (Cliente.email).
        await autoSendVoucherIfEnabled(postVoucherResult?.query, sendVoucher);

        // Subir fotos pendientes por hallazgo (best-effort: una falla no aborta las demás).
        const hallazgosCreados = postVoucherResult?.query?.hallazgos;
        const fallosFotos = await uploadHallazgoFotos(
          hallazgosOrden,
          hallazgosCreados,
          fotosRefsMap.current,
          {
            onError: (i, err) =>
              message.warning(
                `Fotos del hallazgo ${i + 1} no se pudieron subir: ${
                  err?.message || 'error desconocido'
                }`
              ),
          }
        );

        form.resetFields();
        setHallazgos([]);
        fotosRefsMap.current.clear();

        // Confirmación final: con todo terminado, recién ahora avisamos y cerramos.
        notification.success({
          message: postVoucherResult.message || 'Comprobante creado correctamente',
          description:
            fallosFotos > 0
              ? 'El comprobante se guardó. Algunas fotos no se pudieron subir.'
              : 'El comprobante se guardó correctamente.',
        });

        setReload(true);
        setModalVisible(false);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const submitForm = async () => {
    // Marcar "ocupado" desde el primer instante: cubre la revisión IA, el POST
    // y la subida de fotos. Evita que el botón se rehabilite y se dispare un
    // segundo envío mientras hay requests en vuelo. Cada salida del flujo pasa
    // por doPost(), cuyo finally lo vuelve a poner en false; si el flujo se
    // pausa en el drawer de revisión IA, queda en true a propósito hasta que
    // el usuario confirma u omite.
    setSubmitting(true);
    const body = form.getFieldsValue();
    const attentionInit = dayjs(body.attention_init.$d).format(format);
    const attentionFinish = dayjs(body.attention_finish.$d).format(format);

    // Timestamps completos (fecha actual + HH:mm) para MTTR emergencia.
    // Se escriben junto con los strings legacy por compat durante la
    // migración (backfill en 61.0.sql).
    const today = dayjs().format('YYYY-MM-DD');
    const startedAt = `${today} ${attentionInit}:00`;
    const completedAt = `${today} ${attentionFinish}:00`;

    // Extraer IDs de las imágenes subidas desde la respuesta del servidor
    const imageIds =
      body.images
        ?.filter((file) => file.status === 'done' && file.response?.success) // Solo archivos subidos exitosamente
        ?.map((file) => file.response.file.id) || [];

    // Resolver nombres de equipos a IDs FK usando el catálogo cacheado.
    // Si todos resuelven, enviamos id_ascensores para trazabilidad FK.
    // Si alguno no resuelve (texto libre), se omite id_ascensores y el
    // backend usa el CSV de equipment para el PDF.
    const { ids, allResolved } = resolveNamesToIds(body.equipment, clientAscensores);

    // Stripear _key (campo interno de React) antes de mandar al backend
    const hallazgosClean = hallazgos.map(({ _key, ...rest }) => rest);

    const builtBody = buildVoucherBody({
      body,
      attentionInit,
      attentionFinish,
      startedAt,
      completedAt,
      inicioGps,
      imageIds,
      allResolved,
      ids,
      esEmergencia,
      hallazgosClean,
    });

    // Revisión IA best-effort: si no hay textos o no hay cambios, postear directo.
    // Nota: hallazgos (con _key) se pasa para el mapeo de refs de fotos;
    // hallazgosClean (sin _key) es lo que viaja al backend en builtBody.
    const textos = juntarTextosVoucher(builtBody.observations, hallazgosClean);
    if (textos.length === 0) {
      await doPost(builtBody, hallazgos);
      return;
    }

    setRevisionLoading(true);
    setRevisionOpen(true);
    try {
      const { items: mejorados } = await improveTexts(textos);
      const byKey = new Map(mejorados.map((i) => [i.key, i]));
      const merged = textos.map((t) => {
        const r = byKey.get(t.key);
        return {
          key: t.key,
          label: t.label,
          original: t.text,
          improved: r?.improved ?? t.text,
          changed: Boolean(r?.changed),
        };
      });
      const conCambios = merged.filter((m) => m.changed);
      if (conCambios.length === 0) {
        setRevisionOpen(false);
        await doPost(builtBody, hallazgos);
        return;
      }
      setRevisionItems(merged);
      // Guardamos hallazgos (con _key) para el mapeo de fotos al confirmar.
      pendingBodyRef.current = {
        body: builtBody,
        hallazgos: hallazgosClean,
        hallazgosConKey: hallazgos,
      };
    } catch (_e) {
      // IA nunca bloquea el envío: ante fallo, postear directo.
      setRevisionOpen(false);
      await doPost(builtBody, hallazgos);
    } finally {
      setRevisionLoading(false);
    }
  };

  // Confirma las correcciones elegidas: aplica los textos y postea.
  const handleRevisionConfirm = async (finales) => {
    const { body, hallazgos: pendingHallazgos, hallazgosConKey } = pendingBodyRef.current;
    const { observations, hallazgos: hallazgosActualizados } = aplicarTextosVoucher(
      finales,
      body.observations,
      pendingHallazgos
    );
    setRevisionOpen(false);
    // hallazgosConKey (con _key) para mapeo de fotos; hallazgosActualizados (sin _key) para el body.
    await doPost(
      { ...body, observations, hallazgos: hallazgosActualizados },
      hallazgosConKey ?? pendingHallazgos
    );
  };

  // Omitir correcciones: postear con los textos originales.
  const handleRevisionSkip = async () => {
    const { body, hallazgosConKey, hallazgos: pendingHallazgos } = pendingBodyRef.current;
    setRevisionOpen(false);
    await doPost(body, hallazgosConKey ?? pendingHallazgos);
  };

  // Regenera la sugerencia de UN ítem con una instrucción libre del técnico.
  // Devuelve true si se aplicó una nueva sugerencia.
  const handleRegenerate = async (key, instruction) => {
    const item = revisionItems.find((i) => i.key === key);
    if (!item) return false;
    try {
      const { items } = await improveTexts([{ key, text: item.original, instruction }]);
      const r = items.find((i) => i.key === key);
      if (!r) {
        message.warning('No se pudo regenerar la sugerencia. Se mantiene la anterior.');
        return false;
      }
      setRevisionItems((prev) =>
        prev.map((i) =>
          i.key === key ? { ...i, improved: r.improved ?? i.improved, changed: true } : i
        )
      );
      return true;
    } catch (_e) {
      message.warning('No se pudo regenerar la sugerencia. Se mantiene la anterior.');
      return false;
    }
  };
  return (
    <div className="space-y-6">
      {/* Header del formulario */}
      <Card className="shadow-sm border-0 bg-gradient-to-r from-blue-50 to-blue-100">
        <div className="flex items-center space-x-3">
          <div className="bg-blue-600 p-2 rounded-lg">
            <FileTextOutlined className="text-white text-lg" />
          </div>
          <div>
            <Typography.Title level={4} className="mb-0 text-blue-800">
              Comprobante de Atención
            </Typography.Title>
            <Typography.Text type="secondary" className="text-blue-600">
              Registra la atención realizada a los ascensores
            </Typography.Text>
          </div>
        </div>
      </Card>

      {/* Bloque de estado GPS — best-effort, siempre visible */}
      <div>
        {gps.status === 'capturing' && (
          <Space>
            <Spin size="small" />
            <Typography.Text type="secondary">Obteniendo ubicación de la visita…</Typography.Text>
          </Space>
        )}
        {gps.status === 'ok' && (
          <Space>
            <Alert
              type="success"
              showIcon
              message="Ubicación en sitio registrada"
              style={{ flex: 1 }}
            />
            <Button icon={<AimOutlined />} size="small" onClick={registrarUbicacion}>
              Actualizar ubicación
            </Button>
          </Space>
        )}
        {gps.status === 'error' && (
          <Alert
            type="warning"
            showIcon
            message={gpsReasonMessage(gps.reason)}
            description="El comprobante se puede guardar igual sin ubicación."
            action={
              <Button size="small" icon={<AimOutlined />} onClick={registrarUbicacion}>
                Reintentar ubicación
              </Button>
            }
          />
        )}
      </div>

      <Form form={form} onFinish={submitForm} layout="vertical" size="large">
        {/* Información del Cliente y Ascensores */}
        <Card
          className="shadow-sm border-0"
          title={
            <span className="flex items-center space-x-2">
              <BuildOutlined className="text-blue-600" />
              <span>Información del Cliente</span>
            </span>
          }
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={12}>
              <Form.Item
                name="id_client"
                label={
                  <span className="flex items-center space-x-1">
                    <UserOutlined className="text-blue-600" />
                    <span className="font-medium">Cliente</span>
                  </span>
                }
                rules={[{ required: true, message: 'Debe seleccionar un cliente' }]}
              >
                <SelectClient
                  onChange={handleClientChange}
                  placeholder="Seleccionar cliente..."
                  className="rounded-lg"
                />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item
                name="reason"
                label={
                  <span className="flex items-center space-x-1">
                    <FileTextOutlined className="text-green-600" />
                    <span className="font-medium">Razón de la Atención</span>
                  </span>
                }
                rules={[{ required: true, message: 'Debe seleccionar una razón' }]}
              >
                <Select
                  placeholder="Seleccionar razón..."
                  className="rounded-lg"
                  showSearch
                  filterOption={(input, option) =>
                    (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {voluchersReason?.map((item) => (
                    <Select.Option value={item.reason} key={item.reason}>
                      {item.reason}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="equipment"
            label={
              <span className="flex items-center space-x-1">
                <ToolOutlined className="text-orange-600" />
                <span className="font-medium">Ascensores Involucrados</span>
              </span>
            }
            rules={[
              {
                required: true,
                message: 'Debe seleccionar al menos un ascensor',
              },
            ]}
          >
            <AscensorInlineSelect
              mode="multiple"
              idClient={selectedClientId}
              onListLoaded={setClientAscensores}
              placeholder={selectedClientId ? 'Ascensor / equipo' : 'Primero seleccione un cliente'}
              size="large"
            />
          </Form.Item>
        </Card>

        {/* Información de Tiempo y Observaciones */}
        <Card
          className="shadow-sm border-0"
          title={
            <span className="flex items-center space-x-2">
              <ClockCircleOutlined className="text-green-600" />
              <span>Detalles de la Atención</span>
            </span>
          }
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="attention_init"
                label={
                  <span className="flex items-center space-x-1">
                    <ClockCircleOutlined className="text-green-600" />
                    <span className="font-medium">Hora de Llegada</span>
                  </span>
                }
                rules={[
                  {
                    required: true,
                    message: 'Debe ingresar la hora de llegada',
                  },
                ]}
              >
                <TimePicker
                  format={format}
                  className="w-full rounded-lg"
                  placeholder="Seleccionar hora"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="attention_finish"
                label={
                  <span className="flex items-center space-x-1">
                    <ClockCircleOutlined className="text-red-600" />
                    <span className="font-medium">Hora de Salida</span>
                  </span>
                }
                rules={[
                  {
                    required: true,
                    message: 'Debe ingresar la hora de salida',
                  },
                ]}
              >
                <TimePicker
                  format={format}
                  className="w-full rounded-lg"
                  placeholder="Seleccionar hora"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="observations"
            label={
              <span className="flex items-center space-x-1">
                <FileTextOutlined className="text-blue-600" />
                <span className="font-medium">Observaciones</span>
              </span>
            }
            rules={[{ required: true, message: 'Debe ingresar una observación' }]}
          >
            <TextArea
              rows={4}
              placeholder="Describe las actividades realizadas, problemas encontrados, soluciones aplicadas..."
              className="rounded-lg"
            />
          </Form.Item>
        </Card>

        {/* Hallazgos (solo emergencias) */}
        {esEmergencia && (
          <HallazgosVoucherSection
            value={hallazgos}
            onChange={setHallazgos}
            equiposSeleccionados={equiposSeleccionados}
            fotosRefsMap={fotosRefsMap}
          />
        )}

        {/* Imágenes y Estado Final */}
        <Card
          className="shadow-sm border-0"
          title={
            <span className="flex items-center space-x-2">
              <CameraOutlined className="text-purple-600" />
              <span>Evidencia y Estado</span>
            </span>
          }
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={12}>
              <Form.Item
                name="images"
                label={
                  <span className="flex items-center space-x-1">
                    <CameraOutlined className="text-purple-600" />
                    <span className="font-medium">Imágenes (Máximo 3)</span>
                  </span>
                }
                valuePropName="fileList"
                getValueFromEvent={normFile}
                extra="Sube imágenes relacionadas con la atención (máximo 40MB por archivo)"
                rules={[
                  {
                    validator: (_, value) => {
                      if (!value) {
                        return Promise.resolve();
                      }
                      return value.length > 3
                        ? Promise.reject(new Error('Máximo 3 imágenes'))
                        : Promise.resolve();
                    },
                  },
                ]}
              >
                <Upload
                  action={`${API_PATH}/upload/file/attention-voucher`}
                  name="attentionVoucherImage"
                  listType="picture-card"
                  className="w-full"
                  beforeUpload={beforeUpload}
                  onChange={handleUploadChange}
                  maxCount={3}
                  multiple
                  accept="image/*"
                  showUploadList={{
                    showPreviewIcon: true,
                    showRemoveIcon: true,
                    showDownloadIcon: true,
                  }}
                  headers={{
                    Authorization: `Bearer ${token}`,
                  }}
                >
                  <div className="text-center">
                    <UploadOutlined className="text-2xl text-gray-400 mb-2" />
                    <div className="text-gray-500">Subir imagen</div>
                    <div className="text-xs text-gray-400 mt-1">Máx. 3 archivos</div>
                  </div>
                </Upload>
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item
                name="finish_state"
                label={
                  <span className="flex items-center space-x-1">
                    <CheckCircleOutlined className="text-green-600" />
                    <span className="font-medium">Estado Final</span>
                  </span>
                }
                rules={[
                  {
                    required: true,
                    message: 'Debe seleccionar el estado final de los equipos',
                  },
                ]}
              >
                <Select
                  placeholder="Seleccionar estado final..."
                  showSearch
                  options={estadosFinalesAscensor}
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  filterSort={(optionA, optionB) =>
                    (optionA?.label ?? '')
                      .toLowerCase()
                      .localeCompare((optionB?.label ?? '').toLowerCase())
                  }
                  className="rounded-lg"
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* Información del Recepcionista */}
        <Card
          className="shadow-sm border-0"
          title={
            <span className="flex items-center space-x-2">
              <UserOutlined className="text-indigo-600" />
              <span>Información del Recepcionista</span>
            </span>
          }
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12}>
              {/* El RUT va primero: al salir del campo completa el nombre si esa
                  persona ya recibió una visita en este cliente. */}
              <RutRecepcionistaField form={form} idCliente={clienteSeleccionado} />
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="recepcionist_name"
                label={
                  <span className="flex items-center space-x-1">
                    <UserOutlined className="text-indigo-600" />
                    <span className="font-medium">Nombre del Recepcionista</span>
                  </span>
                }
                rules={[
                  {
                    required: true,
                    message: 'Debe ingresar el nombre de quien recibe el comprobante',
                  },
                ]}
              >
                <Input placeholder="Ingresar nombre completo..." className="rounded-lg" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label={
                  <span className="flex items-center space-x-1">
                    <BuildOutlined className="text-indigo-600" />
                    <span className="font-medium">Cargo del Recepcionista</span>
                  </span>
                }
                name="recepcionist_charge"
                rules={[
                  {
                    required: true,
                    message: 'El cargo del recepcionista es obligatorio',
                  },
                ]}
              >
                <Input placeholder="Ej: Recepcionista, Administrador..." className="rounded-lg" />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* Firmas */}
        <Card
          className="shadow-sm border-0"
          title={
            <span className="flex items-center space-x-2">
              <SignatureOutlined className="text-red-600" />
              <span>Firmas Digitales</span>
            </span>
          }
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={12}>
              <Form.Item
                label={
                  <span className="flex items-center space-x-1">
                    <UserOutlined className="text-red-600" />
                    <span className="font-medium">Firma del Recepcionista</span>
                  </span>
                }
                name="recepcionist_signature"
                rules={[
                  {
                    required: true,
                    message: 'La firma del recepcionista es obligatoria',
                  },
                ]}
              >
                {/* Hijo directo del Form.Item: antd le inyecta value/onChange. */}
                <Signature role="recep" />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item
                label={
                  <span className="flex items-center space-x-1">
                    <ToolOutlined className="text-red-600" />
                    <span className="font-medium">Firma del Especialista</span>
                  </span>
                }
                name="specialist_signature"
                rules={[
                  {
                    required: true,
                    message: 'La firma del especialista es obligatoria',
                  },
                ]}
              >
                <Signature role="spec" />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* Botón de envío */}
        <Card className="shadow-sm border-0 bg-gradient-to-r from-green-50 to-green-100">
          <div className="text-center">
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              loading={submitting}
              disabled={submitting}
              className="bg-green-600 hover:bg-green-700 border-green-600 rounded-lg px-8 py-2 h-auto"
              icon={<CheckCircleOutlined />}
            >
              <span className="text-lg font-medium">Crear Comprobante</span>
            </Button>
          </div>
        </Card>
      </Form>

      <RevisarRedaccionDrawer
        open={revisionOpen}
        loading={revisionLoading}
        items={revisionItems}
        onConfirm={handleRevisionConfirm}
        onSkip={handleRevisionSkip}
        onRegenerate={handleRegenerate}
      />
    </div>
  );
}

export default AddAttentionVoucherForm;
